import React, { useCallback, useEffect, useRef, useState } from 'react';
import OtpInput from 'react-otp-input';
import { login_resend_otp, login_verify_otp } from '../services';
import { trackPointer, AlertCircleIcon, CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon } from './AuthLayout';

const OTP_LENGTH = 6;
const SESSION_CLOSED_CODES = [401, 410];
const GENERIC_ERROR = 'Something went wrong. Please try again.';

const OTP_INPUT_STYLE = {
  width: '52px', height: '58px', fontSize: '22px', fontWeight: '600',
  textAlign: 'center', border: '1px solid rgba(10,10,10,.1)',
  borderRadius: '12px', background: '#fff', color: '#1d1d1d',
  outline: 'none', boxShadow: '0 1px 2px rgba(10,10,10,.04)',
  transition: 'border-color .3s, box-shadow .4s', fontFamily: 'inherit',
};

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}

const secondsFromNow = (seconds) => Date.now() + Math.max(Number(seconds) || 0, 0) * 1000;
const secondsUntil = (deadline, now) => Math.max(Math.ceil((deadline - now) / 1000), 0);
const formatClock = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const readFailure = (response) => ({
  message: response?.errors?.message || GENERIC_ERROR,
  code: response?.errors?.error_code,
});

const readRejection = (err) => ({
  message: err?.response?.data?.error?.message || GENERIC_ERROR,
  code: err?.response?.data?.error?.error_code || err?.response?.status,
});

function LoginOtpStep({ challenge, onVerified, onRestart }) {
  const [otp, setOtp] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isSessionClosed, setIsSessionClosed] = useState(false);
  const [expiresAt, setExpiresAt] = useState(() => secondsFromNow(challenge.expires_in));
  const [resendAt, setResendAt] = useState(() => secondsFromNow(challenge.resend_available_in));
  const [resendsRemaining, setResendsRemaining] = useState(challenge.resends_remaining ?? 0);
  const [now, setNow] = useState(Date.now());
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  const expiresIn = secondsUntil(expiresAt, now);
  const resendIn = secondsUntil(resendAt, now);
  const isBusy = isVerifying || isResending;
  const canResend = !isSessionClosed && !isBusy && resendIn === 0 && resendsRemaining > 0;

  const handleFailure = useCallback(({ message, code }) => {
    setInfoMessage('');
    setErrorMessage(message);
    setOtp('');
    if (SESSION_CLOSED_CODES.includes(code)) setIsSessionClosed(true);
  }, []);

  const verify = useCallback(async (code) => {
    if (isSubmittingRef.current || isSessionClosed) return;
    if (code.length !== OTP_LENGTH) { setErrorMessage(`Enter the ${OTP_LENGTH}-digit code.`); return; }
    isSubmittingRef.current = true;
    setIsVerifying(true);
    setErrorMessage('');
    try {
      const response = await login_verify_otp({ challenge_token: challenge.challenge_token, otp_code: code });
      if (response?.statusCode?.status === 1) {
        await onVerified(response.statusCode.response_data?.data);
        return;
      }
      handleFailure(readFailure(response));
    } catch (err) {
      handleFailure(readRejection(err));
    } finally {
      isSubmittingRef.current = false;
      setIsVerifying(false);
    }
  }, [challenge.challenge_token, handleFailure, isSessionClosed, onVerified]);

  const resend = async () => {
    if (!canResend) return;
    setIsResending(true);
    setErrorMessage('');
    setInfoMessage('');
    try {
      const response = await login_resend_otp({ challenge_token: challenge.challenge_token });
      if (response?.statusCode?.status === 1) {
        const data = response.statusCode.response_data?.data || {};
        setOtp('');
        setNow(Date.now());
        setExpiresAt(secondsFromNow(data.expires_in));
        setResendAt(secondsFromNow(data.resend_available_in));
        setResendsRemaining(data.resends_remaining ?? 0);
        setInfoMessage(response.statusCode.response_data?.message || 'A new code has been sent.');
      } else {
        handleFailure(readFailure(response));
      }
    } catch (err) {
      handleFailure(readRejection(err));
    } finally {
      setIsResending(false);
    }
  };

  const onOtpChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, OTP_LENGTH);
    setOtp(digitsOnly);
    setErrorMessage('');
    if (digitsOnly.length === OTP_LENGTH) verify(digitsOnly);
  };

  const onOtpPaste = (event) => {
    event.preventDefault();
    onOtpChange(event.clipboardData.getData('text') || '');
  };

  const renderResend = () => {
    if (isSessionClosed) return null;
    if (isResending) return <span>Sending a new code...</span>;
    if (resendsRemaining <= 0) return <span>No more codes can be sent. Sign in again to get a new one.</span>;
    if (resendIn > 0) return <span aria-live="polite">Resend code in {resendIn}s</span>;
    return <button type="button" className="lg-link" onClick={resend} disabled={!canResend}>Resend code</button>;
  };

  return (
    <div className="lg-wrap">
      <header className="lg-form-header">
        <p className="lg-eyebrow"><ShieldIcon/> Two-step verification</p>
        <h1 className="lg-title">Check your email</h1>
        <p className="lg-lead">
          Enter the {OTP_LENGTH}-digit code we sent to{' '}
          <strong className="lg-otp-email">{challenge.email_hint}</strong>
        </p>
      </header>

      {infoMessage && (
        <div className="lg-alert lg-alert--success" role="status">
          <CheckCircleIcon/> <span>{infoMessage}</span>
        </div>
      )}
      {errorMessage && (
        <div className="lg-alert lg-alert--error" role="alert">
          <AlertCircleIcon/> <span>{errorMessage}</span>
        </div>
      )}

      {!isSessionClosed && (
        <form className="lg-form" noValidate onSubmit={(e) => { e.preventDefault(); verify(otp); }}>
          <div className="lg-otp-wrap">
            <OtpInput
              value={otp}
              onChange={onOtpChange}
              numInputs={OTP_LENGTH}
              inputType="tel"
              shouldAutoFocus
              renderSeparator={<span className="lg-otp-sep"/>}
              renderInput={(props, index) => (
                <input {...props} disabled={isBusy} aria-label={`Digit ${index + 1}`} onPaste={onOtpPaste}
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}/>
              )}
              inputStyle={OTP_INPUT_STYLE}
            />
            <p className="lg-otp-resend" aria-live="polite">
              {expiresIn > 0 ? `Code will expires in 5 mins` : 'This code has expired. Request a new one.'}
            </p>
          </div>

          <div className="lg-otp-resend">{renderResend()}</div>

          <button type="submit" className="lg-submit lg-spot"
            disabled={isBusy || otp.length !== OTP_LENGTH} aria-busy={isVerifying} onPointerMove={trackPointer}>
            {isVerifying ? (
              <span className="lg-submit-label"><span className="lg-spin" aria-hidden="true"/> Verifying...</span>
            ) : (
              <span className="lg-submit-label">Verify and sign in <ArrowRightIcon/></span>
            )}
            {isVerifying && <i className="lg-scan" aria-hidden="true"/>}
          </button>
        </form>
      )}

      <p className="lg-signup">
        <button type="button" className="lg-back-btn" onClick={onRestart} disabled={isVerifying}>
          <ArrowLeftIcon/> Back to sign in
        </button>
      </p>
    </div>
  );
}

export default LoginOtpStep;
